import * as React from 'react'
import {
  AlertCircle as WarningCircleIcon,
  ChevronLeft as CaretLeftIcon,
  ChevronRight as CaretRightIcon,
  Folder as FolderIcon,
  RefreshCw as RefreshIcon,
  Search as MagnifyingGlassIcon,
  X as XIcon
} from 'lucide-react'
import {
  useGetPlantProviderFolderNodeQuery,
  useGetPlantProviderTagsQuery,
  useGetPlantUdtFolderNodeQuery,
  useLazyGetPlantLiveTagValuesByIdQuery,
  type PlantComputeHost,
  type PlantFolderSubfolderEntry,
  type PlantProviderTag,
  type PlantProviderUdtInstanceEntry,
  type PlantTagProvider,
  type PlantTagValue,
  type PlantUdtNestedInstanceEntry
} from '@axilon-apps/domains-ontology-ui-state'
import { Empty } from '../../components/primitives/Empty'
import { useDebouncedValue } from '../../lib/use-debounced-value'
import { cn } from '../../lib/utils'
import { computeTypeKey } from './compute-icon'
import { loadIncrementalBatches } from './incremental-tag-value-loader'

const PAGE_SIZE = 30

type ViewMode = 'flat' | 'explorer'

/**
 * Inline browser panel for a single `TagProvider`. Drilldown opened
 * from `ComputeHostDetailPage` when `?provider=<id>` is set.
 *
 * Wraps two views: `FlatTagView` (paginated, searchable table) and
 * `ExplorerTagView` (file-explorer-style folder navigation). A
 * checkbox in the panel header toggles between them. Both views load one
 * value snapshot in bounded batches for the tags they currently show;
 * each response paints immediately, with no recurring polling.
 *
 * `selectedTagName` powers the cross-layer deep-link from the PLC
 * page's SCADA-tag chips: when set, the panel forces flat view, the
 * search box is pre-filled with the tag name, and the matching row
 * is highlighted so the user lands on the tag instead of staring at
 * the top of an unfiltered list.
 */
export function TagProviderBrowser({
  host,
  provider,
  selectedTagName = null
}: {
  host: PlantComputeHost
  provider: PlantTagProvider
  selectedTagName?: string | null
}): React.ReactElement {
  const hasSelection = selectedTagName !== null && selectedTagName.length > 0
  const [viewMode, setViewMode] = React.useState<ViewMode>(hasSelection ? 'flat' : 'explorer')
  const [refreshKey, setRefreshKey] = React.useState(0)

  // Reset view mode back to the default when the user switches
  // providers, so they always land in the same starting view. When
  // the user followed a `?tag=` deep-link we land on flat view so the
  // pre-filled search has somewhere to render.
  React.useEffect(() => {
    setViewMode(hasSelection ? 'flat' : 'explorer')
  }, [provider.id, hasSelection])

  const isIgnitionHost = computeTypeKey(host.compute_type) === 'ignition'
  // Direct-OPC providers (protocol `opc-ua`) carry registry-readable tags even
  // on non-Ignition hosts, so the value column stays useful for them.
  const showValueColumn = isIgnitionHost || provider.protocol === 'opc-ua'

  return (
    <section className="rounded-xl border bg-card text-card-foreground shadow-sm">
      <header className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
        <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">Tags</h3>
        <div className="ml-auto flex items-center gap-3">
          {showValueColumn ? (
            <button
              type="button"
              onClick={() => setRefreshKey((key) => key + 1)}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-line px-2.5 py-1 text-11 text-ink-2 transition-colors hover:bg-bg-sunk hover:text-ink focus-visible:border-brand focus-visible:outline-none"
              aria-label="Refresh tag values"
            >
              <RefreshIcon className="size-3" aria-hidden />
              Refresh values
            </button>
          ) : null}
          <label className="flex cursor-pointer select-none items-center gap-1.5 text-11 text-ink-3">
            <input
              type="checkbox"
              checked={viewMode === 'explorer'}
              onChange={(event) => setViewMode(event.target.checked ? 'explorer' : 'flat')}
              className="size-3 cursor-pointer accent-brand"
            />
            Folder explorer
          </label>
        </div>
      </header>

      {viewMode === 'flat' ? (
        <FlatTagView
          provider={provider}
          showValueColumn={showValueColumn}
          selectedTagName={selectedTagName}
          refreshKey={refreshKey}
        />
      ) : (
        <ExplorerTagView
          provider={provider}
          showValueColumn={showValueColumn}
          refreshKey={refreshKey}
        />
      )}
    </section>
  )
}

// ─── Flat view ─────────────────────────────────────────────────────

function FlatTagView({
  provider,
  showValueColumn,
  selectedTagName = null,
  refreshKey
}: {
  provider: PlantTagProvider
  showValueColumn: boolean
  refreshKey: number
  /**
   * Tag name to pre-fill into the search box and visually highlight
   * in the results table. Powers the PLC-page SCADA-tag deep-link
   * (`?tag=<name>` on the host URL). `null` when the user opened the
   * provider on their own.
   */
  selectedTagName?: string | null
}): React.ReactElement {
  const [searchInput, setSearchInput] = React.useState(selectedTagName ?? '')
  const debouncedSearch = useDebouncedValue(searchInput, 250)
  const trimmedSearch = debouncedSearch.trim()

  const [currentPage, setCurrentPage] = React.useState(0)

  // Reset back to page 1 whenever the search query changes — keeps the
  // user from staring at "Page 7 of 1" after typing a narrow query.
  React.useEffect(() => {
    setCurrentPage(0)
  }, [trimmedSearch])

  // Re-seed the search box from `selectedTagName` whenever the
  // deep-link target changes. Without this, navigating from one PLC
  // chip to another for the same provider would silently leave the
  // old search in place because the panel doesn't unmount between
  // hops.
  React.useEffect(() => {
    setSearchInput(selectedTagName ?? '')
    setCurrentPage(0)
  }, [selectedTagName])

  // Same reset when the parent flips between providers without
  // unmounting the panel — except respect `selectedTagName` if the
  // deep-link is still pointing at a tag in the new provider.
  React.useEffect(() => {
    setSearchInput(selectedTagName ?? '')
    setCurrentPage(0)
    // selectedTagName intentionally omitted; the dedicated effect
    // above handles deep-link changes within the same provider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider.id])

  const queryResult = useGetPlantProviderTagsQuery({
    providerId: provider.id,
    search: trimmedSearch || undefined,
    page: currentPage,
    pageSize: PAGE_SIZE
  })
  const { data, isLoading, isFetching, isError } = queryResult

  const tags = data?.tags ?? []
  const totalTags = data?.totalTags ?? 0
  const totalPages = Math.max(1, Math.ceil(totalTags / PAGE_SIZE))
  const hasSearchQuery = trimmedSearch.length > 0

  const { valuesByPath } = useLiveTagValues({
    tags,
    enabled: showValueColumn,
    refreshKey
  })

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <div className="min-w-50 flex-1">
          <SearchInput value={searchInput} onChange={setSearchInput} />
        </div>
        <span className="whitespace-nowrap text-xs text-ink-3" aria-live="polite">
          {flatSummaryText({ isLoading, totalTags, hasSearchQuery, trimmedSearch })}
        </span>
        {totalPages > 1 && (
          <PageNav
            currentPage={currentPage}
            totalPages={totalPages}
            isFetching={isFetching}
            onPrev={() => setCurrentPage((p) => Math.max(0, p - 1))}
            onNext={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
          />
        )}
      </div>

      <div className="px-4 pb-4">
        {isError ? (
          <Empty
            title="Couldn't load tags"
            hint="The proxy may be unreachable. Try clearing the search or reopening this provider."
            icon={WarningCircleIcon}
          />
        ) : isLoading ? (
          <div className="text-sm text-ink-3">Loading tags…</div>
        ) : tags.length === 0 ? (
          hasSearchQuery ? (
            <EmptySearchState search={trimmedSearch} onClear={() => setSearchInput('')} />
          ) : (
            <div className="text-sm text-ink-3">This provider has no tags in the ontology.</div>
          )
        ) : (
          <TagTable
            tags={tags}
            valuesByPath={valuesByPath}
            showValueColumn={showValueColumn}
            showFolderColumn
            highlightedTagName={selectedTagName}
          />
        )}
      </div>
    </>
  )
}

function flatSummaryText({
  isLoading,
  totalTags,
  hasSearchQuery,
  trimmedSearch
}: {
  isLoading: boolean
  totalTags: number
  hasSearchQuery: boolean
  trimmedSearch: string
}): string {
  if (isLoading) return 'Loading…'
  if (hasSearchQuery) {
    const noun = totalTags === 1 ? 'tag matches' : 'tags match'
    return `${totalTags.toLocaleString()} ${noun} "${trimmedSearch}"`
  }
  const noun = totalTags === 1 ? 'tag' : 'tags'
  return `${totalTags.toLocaleString()} ${noun}`
}

// ─── Explorer view ─────────────────────────────────────────────────

/**
 * One step on the breadcrumb stack. Navigation runs in two modes:
 *
 *   1. Tag-folder mode (no `udtRoot`/`udtNestedInstance` segments in
 *      the stack): the active query is `getPlantProviderFolderNode`,
 *      pathed by joining `tagFolder` names with `/`.
 *   2. UDT mode (most-recent UDT-context segment is non-null): the
 *      active query is `getPlantUdtFolderNode`, with `definitionId`
 *      drawn from that segment and `subPath` built by joining the
 *      `udtSubfolder` segments accumulated after it. Clicking a
 *      nested UDT-instance member pushes a `udtNestedInstance`
 *      segment that resets the definition pointer to the referenced
 *      `:UDTDefinition`.
 *
 * `instanceId` carries the *root* UDT instance for live-poll path
 * construction (`[<provider>]<instanceFullPath>/<member-name>`). It
 * survives nested-instance drilldowns because the readable
 * Ignition path is always rooted at the top-level instance.
 */
type PathSegment =
  | { kind: 'tagFolder'; name: string }
  | {
      kind: 'udtRoot'
      name: string
      instanceId: string
      definitionId: string
      instanceFullPath: string
    }
  | { kind: 'udtSubfolder'; name: string }
  /**
   * Drilldown into a nested `:UDTInstance` HAS_MEMBER from the current
   * UDT context. Carries the nested instance's own id so the next
   * `getPlantUdtFolderNode` call resolves children against that
   * runtime instance (which is where the leaf `:Tag` rows with the
   * canonical Ignition `tag_path` actually live — see
   * `extractUdtNestedInstanceEntries`).
   */
  | {
      kind: 'udtNestedInstance'
      name: string
      nestedInstanceId: string
      definitionId: string
    }

/**
 * Read-only projection of a path stack: what (if any) UDT context is
 * active, and what `(instanceId, definitionId, subPath)` to pass to
 * `getPlantUdtFolderNode`. Tag-folder navigation when `udtContext`
 * is `null` — `tagFolderPath` is the slash-joined name list.
 */
interface ExplorerLocation {
  tagFolderPath: string
  udtContext: {
    instanceId: string
    instanceFullPath: string
    definitionId: string
    subPath: string
  } | null
}

function resolveLocation(path: PathSegment[]): ExplorerLocation {
  // Walk the stack right-to-left to find the active UDT context —
  // the most recent `udtRoot` / `udtNestedInstance` "wins". Segments
  // before that contribute to `tagFolderPath`; udtSubfolder segments
  // after it contribute to `subPath`.
  let lastUdtIdx = -1
  for (let i = path.length - 1; i >= 0; i--) {
    const seg = path[i]
    if (seg.kind === 'udtRoot' || seg.kind === 'udtNestedInstance') {
      lastUdtIdx = i
      break
    }
  }
  if (lastUdtIdx === -1) {
    const tagFolderPath = path
      .filter((s): s is Extract<PathSegment, { kind: 'tagFolder' }> => s.kind === 'tagFolder')
      .map((s) => s.name)
      .join('/')
    return { tagFolderPath, udtContext: null }
  }
  const tagFolderPath = path
    .slice(0, lastUdtIdx)
    .filter((s): s is Extract<PathSegment, { kind: 'tagFolder' }> => s.kind === 'tagFolder')
    .map((s) => s.name)
    .join('/')
  // Find the original `udtRoot` to recover `instanceFullPath` for any
  // legacy callers that still want the human-readable instance path.
  let rootInstanceId = ''
  let instanceFullPath = ''
  for (const seg of path) {
    if (seg.kind === 'udtRoot') {
      rootInstanceId = seg.instanceId
      instanceFullPath = seg.instanceFullPath
      break
    }
  }
  const active = path[lastUdtIdx]
  if (active.kind !== 'udtRoot' && active.kind !== 'udtNestedInstance') {
    return { tagFolderPath, udtContext: null }
  }
  // The effective instance id is the most-recent nested instance,
  // not the root. Pre-refactor the explorer pinned `instanceId` to
  // the root and re-walked the definition tree to find children of
  // nested instances — but the runtime `:Tag` children of a nested
  // instance live under that nested instance's own HAS_MEMBER edges,
  // and the definition template doesn't carry the canonical
  // `[Provider]…/<member>` `tag_path` the live-data poller needs.
  // Using the nested instance's id here makes the cypher's
  // `HAS_MEMBER` walk land on the correct runtime children directly.
  const effectiveInstanceId =
    active.kind === 'udtNestedInstance' ? active.nestedInstanceId : rootInstanceId
  const definitionId = active.definitionId
  const subPath = path
    .slice(lastUdtIdx + 1)
    .filter((s): s is Extract<PathSegment, { kind: 'udtSubfolder' }> => s.kind === 'udtSubfolder')
    .map((s) => s.name)
    .join('/')
  return {
    tagFolderPath,
    udtContext: { instanceId: effectiveInstanceId, instanceFullPath, definitionId, subPath }
  }
}

function ExplorerTagView({
  provider,
  showValueColumn,
  refreshKey
}: {
  provider: PlantTagProvider
  showValueColumn: boolean
  refreshKey: number
}): React.ReactElement {
  const [path, setPath] = React.useState<PathSegment[]>([])

  // Pop back to root when the user switches providers — old paths
  // wouldn't make sense in the new provider's folder structure.
  React.useEffect(() => {
    setPath([])
  }, [provider.id])

  const location = React.useMemo(() => resolveLocation(path), [path])

  const tagFolderQuery = useGetPlantProviderFolderNodeQuery(
    {
      providerId: provider.id,
      path: location.tagFolderPath
    },
    { skip: location.udtContext !== null }
  )
  const udtFolderQuery = useGetPlantUdtFolderNodeQuery(
    location.udtContext
      ? {
          udtInstanceId: location.udtContext.instanceId,
          definitionId: location.udtContext.definitionId,
          subPath: location.udtContext.subPath,
          providerName: provider.name,
          instanceFullPath: location.udtContext.instanceFullPath
        }
      : ({} as never),
    { skip: location.udtContext === null }
  )

  const isUdtMode = location.udtContext !== null
  const isLoading = isUdtMode ? udtFolderQuery.isLoading : tagFolderQuery.isLoading
  const isError = isUdtMode ? udtFolderQuery.isError : tagFolderQuery.isError

  const rawSubfolders: PlantFolderSubfolderEntry[] = isUdtMode
    ? (udtFolderQuery.data?.subfolders ?? [])
    : (tagFolderQuery.data?.subfolders ?? [])
  const directTags: PlantProviderTag[] = isUdtMode
    ? (udtFolderQuery.data?.directMembers ?? [])
    : (tagFolderQuery.data?.directTags ?? [])
  const directUdtInstances: PlantProviderUdtInstanceEntry[] = isUdtMode
    ? []
    : (tagFolderQuery.data?.directUdtInstances ?? [])
  const directNestedInstances: PlantUdtNestedInstanceEntry[] = isUdtMode
    ? (udtFolderQuery.data?.directNestedInstances ?? [])
    : []
  // In UDT mode the subfolder cypher counts any child whose
  // relative-to-subPath name still has a slash — including nested
  // `:UDTInstance` children whose memberName has a folder prefix
  // (e.g. `Limits/Limit5`). Those nested instances also surface as
  // dedicated rows in `directNestedInstances` at the same first
  // segment, so without dedup the user sees both a "Limits" folder
  // and a "Limits/…" set of nested instance rows at the next
  // subfolder level. Filter the duplicates out so the UDT view
  // matches the tag-folder view: a name only appears once, either
  // as a nested instance OR as a subfolder.
  const subfolders: PlantFolderSubfolderEntry[] = React.useMemo(() => {
    if (!isUdtMode) return rawSubfolders
    const nestedNames = new Set(directNestedInstances.map((e) => e.memberName))
    return rawSubfolders.filter((f) => !nestedNames.has(f.name))
  }, [isUdtMode, rawSubfolders, directNestedInstances])

  const { valuesByPath } = useLiveTagValues({
    tags: directTags,
    enabled: showValueColumn,
    refreshKey
  })

  const atRoot = path.length === 0

  const onEnterSubfolder = (name: string) => {
    setPath((p) =>
      isUdtMode ? [...p, { kind: 'udtSubfolder', name }] : [...p, { kind: 'tagFolder', name }]
    )
  }
  const onEnterUdtInstance = (entry: PlantProviderUdtInstanceEntry) => {
    if (!entry.definitionId) return
    const folderPrefix = location.tagFolderPath
    const instanceFullPath = folderPrefix
      ? `${folderPrefix}/${entry.instanceName}`
      : entry.instanceName
    setPath((p) => [
      ...p,
      {
        kind: 'udtRoot',
        name: entry.instanceName,
        instanceId: entry.id,
        definitionId: entry.definitionId as string,
        instanceFullPath
      }
    ])
  }
  const onEnterNestedInstance = (entry: PlantUdtNestedInstanceEntry) => {
    // Need the runtime instance URN to walk into the right
    // `:UDTInstance` HAS_MEMBER subtree — that's where the leaf
    // `:Tag` rows with the canonical Ignition `tag_path` live.
    // `nestedDefinitionId` is kept on the segment for display only;
    // it's not required for navigation any more (see
    // `resolveLocation`).
    if (!entry.nestedInstanceId) return
    setPath((p) => [
      ...p,
      {
        kind: 'udtNestedInstance',
        name: entry.memberName,
        nestedInstanceId: entry.nestedInstanceId,
        definitionId: entry.nestedDefinitionId ?? ''
      }
    ])
  }

  const totalFolderRows =
    subfolders.length + directUdtInstances.length + directNestedInstances.length

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setPath((p) => p.slice(0, -1))}
          disabled={atRoot}
          aria-label="Go up one folder"
          className={cn(
            'flex items-center gap-1 rounded-md border px-2.5 py-1 text-11 transition-colors',
            atRoot
              ? 'cursor-not-allowed border-line text-ink-4'
              : 'cursor-pointer border-line text-ink-2 hover:bg-bg-sunk hover:text-ink focus-visible:border-brand focus-visible:outline-none'
          )}
        >
          <CaretLeftIcon size={10} />
          Back
        </button>
        <Breadcrumb path={path} onJumpTo={(depth) => setPath((p) => p.slice(0, depth))} />
        <span className="ml-auto whitespace-nowrap text-xs text-ink-3" aria-live="polite">
          {explorerSummary({ subfolders: totalFolderRows, tags: directTags.length })}
        </span>
      </div>

      <div className="px-4 pb-4">
        {isError ? (
          <Empty
            title="Couldn't load tags"
            hint="The proxy may be unreachable. Try reopening this provider."
            icon={WarningCircleIcon}
          />
        ) : isLoading ? (
          <div className="text-sm text-ink-3">Loading tags…</div>
        ) : totalFolderRows === 0 && directTags.length === 0 ? (
          <div className="text-sm text-ink-3">
            {atRoot ? 'This provider has no tags in the ontology.' : 'This folder is empty.'}
          </div>
        ) : (
          <FolderTable
            subfolders={subfolders}
            directUdtInstances={directUdtInstances}
            directNestedInstances={directNestedInstances}
            tags={directTags}
            valuesByPath={valuesByPath}
            showValueColumn={showValueColumn}
            onEnterSubfolder={onEnterSubfolder}
            onEnterUdtInstance={onEnterUdtInstance}
            onEnterNestedInstance={onEnterNestedInstance}
          />
        )}
      </div>
    </>
  )
}

function explorerSummary({ subfolders, tags }: { subfolders: number; tags: number }): string {
  if (subfolders === 0 && tags === 0) return 'Empty'
  const parts: string[] = []
  if (subfolders > 0) {
    parts.push(`${subfolders} ${subfolders === 1 ? 'folder' : 'folders'}`)
  }
  if (tags > 0) {
    parts.push(`${tags} ${tags === 1 ? 'tag' : 'tags'}`)
  }
  return parts.join(' · ')
}

function Breadcrumb({
  path,
  onJumpTo
}: {
  path: PathSegment[]
  onJumpTo: (depth: number) => void
}): React.ReactElement {
  return (
    <nav
      className="flex min-w-0 flex-wrap items-center gap-1 text-11"
      aria-label="Folder breadcrumb"
    >
      <button
        type="button"
        onClick={() => onJumpTo(0)}
        className={cn(
          'cursor-pointer rounded px-1 py-0.5 font-mono',
          path.length === 0
            ? 'text-ink'
            : 'text-ink-3 hover:bg-bg-sunk hover:text-ink focus-visible:bg-bg-sunk focus-visible:outline-none'
        )}
        disabled={path.length === 0}
      >
        root
      </button>
      {path.map((segment, index) => {
        const isLast = index === path.length - 1
        const isUdt = segment.kind === 'udtRoot' || segment.kind === 'udtNestedInstance'
        return (
          <React.Fragment key={`${index}-${segment.kind}-${segment.name}`}>
            <span className="text-ink-4">/</span>
            {isLast ? (
              <span className="truncate font-mono text-ink">
                {segment.name}
                {isUdt ? <UdtBadge /> : null}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onJumpTo(index + 1)}
                className="cursor-pointer rounded px-1 py-0.5 font-mono text-ink-3 hover:bg-bg-sunk hover:text-ink focus-visible:bg-bg-sunk focus-visible:outline-none"
              >
                {segment.name}
                {isUdt ? <UdtBadge /> : null}
              </button>
            )}
          </React.Fragment>
        )
      })}
    </nav>
  )
}

/**
 * Small inline pill the explorer renders on UDT-instance and nested
 * UDT-instance folder rows (and on the breadcrumb segments that
 * represent them) so the user can tell at a glance that drilling in
 * crosses into a UDT template — not just another tag folder.
 */
function UdtBadge(): React.ReactElement {
  return (
    <span className="ml-1.5 inline-block rounded-sm border border-line px-1 align-middle text-9 font-medium uppercase tracking-wider text-ink-3">
      UDT
    </span>
  )
}

function FolderTable({
  subfolders,
  directUdtInstances,
  directNestedInstances,
  tags,
  valuesByPath,
  showValueColumn,
  onEnterSubfolder,
  onEnterUdtInstance,
  onEnterNestedInstance
}: {
  subfolders: PlantFolderSubfolderEntry[]
  directUdtInstances: PlantProviderUdtInstanceEntry[]
  directNestedInstances: PlantUdtNestedInstanceEntry[]
  tags: PlantProviderTag[]
  valuesByPath: Map<string, PlantTagValue>
  showValueColumn: boolean
  onEnterSubfolder: (folderName: string) => void
  onEnterUdtInstance: (entry: PlantProviderUdtInstanceEntry) => void
  onEnterNestedInstance: (entry: PlantUdtNestedInstanceEntry) => void
}): React.ReactElement {
  return (
    <div className="overflow-hidden rounded-md border border-line">
      <table className="w-full table-fixed text-left text-xs">
        <colgroup>
          <col />
          <col className="w-20" />
          {showValueColumn && <col className="w-55" />}
        </colgroup>
        <thead className="border-b border-line bg-bg-sunk/60 text-ink-3">
          <tr>
            <th className="px-2 py-1 text-10 font-medium uppercase tracking-wider">Name</th>
            <th className="px-2 py-1 text-10 font-medium uppercase tracking-wider">Data</th>
            {showValueColumn && (
              <th className="px-2 py-1 text-right text-10 font-medium uppercase tracking-wider">
                Value
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {subfolders.map((folder) => (
            <FolderRow
              key={`tf-${folder.name}`}
              name={folder.name}
              tagCount={folder.tagCount}
              isUdt={false}
              typeLabel="Folder"
              onEnter={() => onEnterSubfolder(folder.name)}
              showValueColumn={showValueColumn}
            />
          ))}
          {directUdtInstances.map((entry) => (
            <FolderRow
              key={`udt-${entry.id}`}
              name={entry.instanceName}
              tagCount={entry.memberCount}
              isUdt
              typeLabel={entry.definitionName ?? 'UDT instance'}
              onEnter={() => onEnterUdtInstance(entry)}
              showValueColumn={showValueColumn}
            />
          ))}
          {directNestedInstances.map((entry) => (
            <FolderRow
              key={`nested-${entry.id}`}
              name={entry.memberName}
              tagCount={entry.memberCount}
              isUdt
              typeLabel={entry.nestedDefinitionName ?? 'UDT instance'}
              onEnter={() => onEnterNestedInstance(entry)}
              showValueColumn={showValueColumn}
            />
          ))}
          {tags.map((tag) => {
            const path = showValueColumn ? tagReadPath(tag) : undefined
            const value = path ? valuesByPath.get(path) : undefined
            return (
              <TagRow
                key={tag.id}
                tag={tag}
                value={value}
                showFolderColumn={false}
                showValueColumn={showValueColumn}
              />
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function FolderRow({
  name,
  tagCount,
  isUdt,
  typeLabel,
  onEnter,
  showValueColumn
}: {
  name: string
  tagCount: number
  isUdt: boolean
  /**
   * Label shown in the "Data" column. `'Folder'` for a regular
   * tag-folder subfolder; the UDT definition's fully-qualified type
   * (e.g. `BKP/Delkor/CaseErector`) for a UDT instance / nested UDT
   * instance, so users can see what the instance implements at a
   * glance.
   */
  typeLabel: string
  onEnter: () => void
  showValueColumn: boolean
}): React.ReactElement {
  return (
    <tr
      onClick={onEnter}
      className="cursor-pointer border-b border-line last:border-b-0 hover:bg-bg-sunk/60"
    >
      <td className="px-2 py-1">
        <span className="flex min-w-0 items-center gap-2">
          <FolderIcon size={12} className="shrink-0 text-ink-3" />
          <span className="min-w-0 flex-1 truncate font-mono text-ink">{name}</span>
          {isUdt ? <UdtBadge /> : null}
          <span className="shrink-0 text-10 text-ink-4">
            {tagCount} {tagCount === 1 ? 'tag' : 'tags'}
          </span>
        </span>
      </td>
      <td className="truncate px-2 py-1 font-mono text-11 text-ink-3" title={typeLabel}>
        {typeLabel}
      </td>
      {showValueColumn && <td className="px-2 py-1 text-right text-ink-4">—</td>}
    </tr>
  )
}

// ─── Shared bits ──────────────────────────────────────────────────

// Every displayed row is a graph Tag, so acquisition resolves only through
// `tags.node_id = :Tag.id`. Unregistered graph tags remain visible with an
// explicit read error instead of claiming a path-matched registry row. Values
// load once in bounded batches; each batch updates the table immediately.
function useLiveTagValues({
  tags,
  enabled,
  refreshKey
}: {
  tags: PlantProviderTag[]
  /** Value column visible — allow one graph-ID snapshot. */
  enabled: boolean
  /** Incremented by the explicit Refresh values button. */
  refreshKey: number
}): { valuesByPath: Map<string, PlantTagValue> } {
  const registryTags = React.useMemo(
    () => tags.filter((tag) => Boolean(tag.id.trim() && tag.tag_path?.trim())),
    [tags]
  )
  const registryIds = React.useMemo(() => registryTags.map((t) => t.id), [registryTags])
  const canLoadRegistry = enabled && registryIds.length > 0
  const pathById = React.useMemo(
    () => new Map(registryTags.map((tag) => [tag.id, tag.tag_path?.trim() ?? ''])),
    [registryTags]
  )
  const [loadRegistryValues] = useLazyGetPlantLiveTagValuesByIdQuery()
  const [valuesByPath, setValuesByPath] = React.useState<Map<string, PlantTagValue>>(
    () => new Map()
  )

  React.useEffect(() => {
    let active = true
    setValuesByPath(new Map())

    const mergeValues = (values: readonly PlantTagValue[]) => {
      if (!active || values.length === 0) return
      setValuesByPath((current) => {
        const next = new Map(current)
        for (const value of values) next.set(value.path, value)
        return next
      })
    }
    const failedPathValues = (paths: readonly string[], error: unknown): PlantTagValue[] => {
      const reason = tagValueLoadError(error)
      return paths.map((path) => ({ path, value: undefined, error: reason }))
    }

    if (canLoadRegistry && registryIds.length > 0) {
      void loadIncrementalBatches({
        items: registryIds,
        shouldContinue: () => active,
        load: (nodeIds) => loadRegistryValues({ node_ids: nodeIds }).unwrap(),
        onBatch: (response) => {
          const values: PlantTagValue[] = []
          for (const value of response.tags) {
            const path = pathById.get(value.nodeId)
            if (!path) continue
            values.push({
              path,
              value: value.value,
              ...(value.quality ? { quality: value.quality } : {}),
              ...(value.timestamp ? { timestamp: value.timestamp } : {}),
              ...(value.error
                ? { error: value.error }
                : value.unregistered
                  ? { error: 'Tag is not registered for reads' }
                  : {})
            })
          }
          mergeValues(values)
        },
        onError: (error, nodeIds) => {
          const paths = nodeIds
            .map((id) => pathById.get(id))
            .filter((path): path is string => !!path)
          mergeValues(failedPathValues(paths, error))
        }
      })
    }

    return () => {
      active = false
    }
  }, [canLoadRegistry, registryIds, pathById, loadRegistryValues, refreshKey])

  return { valuesByPath }
}

function tagValueLoadError(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>
    const data = record.data
    if (data && typeof data === 'object') {
      const message = (data as Record<string, unknown>).error
      if (typeof message === 'string' && message.trim()) return message
    }
    if (typeof record.error === 'string' && record.error.trim()) return record.error
  }
  return 'Tag value read failed'
}

function SearchInput({
  value,
  onChange
}: {
  value: string
  onChange: (next: string) => void
}): React.ReactElement {
  return (
    <div className="relative">
      <MagnifyingGlassIcon
        size={14}
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-3"
        aria-hidden
      />
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Search tags by name or folder…"
        className="w-full rounded-lg border border-line bg-card py-2 pl-9 pr-9 text-sm shadow-sm outline-none placeholder:text-ink-4 focus-visible:border-brand"
      />
      {value.length > 0 && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 grid size-6 -translate-y-1/2 cursor-pointer place-items-center rounded text-ink-3 hover:bg-bg-sunk hover:text-ink"
        >
          <XIcon size={12} />
        </button>
      )}
    </div>
  )
}

function EmptySearchState({
  search,
  onClear
}: {
  search: string
  onClear: () => void
}): React.ReactElement {
  return (
    <div className="flex flex-col items-center gap-2 py-6 text-center">
      <div className="text-sm text-ink-3">
        No tags match <span className="font-mono text-ink-2">&quot;{search}&quot;</span>.
      </div>
      <button
        type="button"
        onClick={onClear}
        className="cursor-pointer text-xs text-brand underline-offset-2 hover:underline"
      >
        Clear search
      </button>
    </div>
  )
}

function TagTable({
  tags,
  valuesByPath,
  showValueColumn,
  showFolderColumn,
  highlightedTagName = null
}: {
  tags: PlantProviderTag[]
  valuesByPath: Map<string, PlantTagValue>
  showValueColumn: boolean
  showFolderColumn: boolean
  /**
   * When non-null, rows whose `tag.name` matches this string render
   * with a brand-tinted background so the user can spot the
   * deep-linked tag inside the (potentially long) result set. Used
   * only by the SCADA-tag deep-link from the PLC page today.
   */
  highlightedTagName?: string | null
}): React.ReactElement {
  return (
    <div className="overflow-hidden rounded-md border border-line">
      <table className="w-full table-fixed text-left text-xs">
        <colgroup>
          <col className={showFolderColumn ? 'w-[30%]' : undefined} />
          {showFolderColumn && <col className="w-[30%]" />}
          <col className="w-20" />
          {showValueColumn && <col className="w-55" />}
        </colgroup>
        <thead className="border-b border-line bg-bg-sunk/60 text-ink-3">
          <tr>
            <th className="px-2 py-1 text-10 font-medium uppercase tracking-wider">Tag</th>
            {showFolderColumn && (
              <th className="px-2 py-1 text-10 font-medium uppercase tracking-wider">Folder</th>
            )}
            <th className="px-2 py-1 text-10 font-medium uppercase tracking-wider">Data</th>
            {showValueColumn && (
              <th className="px-2 py-1 text-right text-10 font-medium uppercase tracking-wider">
                Value
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {tags.map((tag) => {
            const path = showValueColumn ? tagReadPath(tag) : undefined
            const value = path ? valuesByPath.get(path) : undefined
            const isHighlighted = highlightedTagName !== null && tag.name === highlightedTagName
            return (
              <TagRow
                key={tag.id}
                tag={tag}
                value={value}
                showFolderColumn={showFolderColumn}
                showValueColumn={showValueColumn}
                highlighted={isHighlighted}
              />
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function TagRow({
  tag,
  value,
  showFolderColumn,
  showValueColumn,
  highlighted = false
}: {
  tag: PlantProviderTag
  value: PlantTagValue | undefined
  showFolderColumn: boolean
  showValueColumn: boolean
  /**
   * When true, render this row with a brand-tinted background to
   * mark it as the deep-linked target. See `TagTable.highlightedTagName`.
   */
  highlighted?: boolean
}): React.ReactElement {
  const leaf = leafName(tag.name)
  const folder = tag.folder?.trim() ?? ''
  const isUdtMember = tag.ignition_tag_type === 'UDTMember'
  // Ignition data type (`Boolean` / `Float4` / …) takes priority.
  // Legacy UDTMember rows in the graph carry only the value-source
  // kind (`opc` / `memory` / `expression` / …) because the udt-mapper
  // didn't persist `data_type` before this PR — fall back to the
  // member kind so users still see *something* instead of `—`.
  // Plain `:Tag` rows already carry both, so the fallback also picks
  // up `ignition_tag_type` cleanly when `data_type` happens to be unset.
  const memberSourceKind = tag.member_type ?? (isUdtMember ? undefined : tag.ignition_tag_type)
  const typeLabel = tag.data_type ?? memberSourceKind
  // Auto-scroll the highlighted row into view when it first paints.
  // Only fires for the deep-linked target so regular browsing is
  // never reflowed underneath the user.
  const rowRef = React.useRef<HTMLTableRowElement | null>(null)
  React.useEffect(() => {
    if (highlighted && rowRef.current) {
      rowRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [highlighted])
  return (
    <tr
      ref={rowRef}
      title={tag.name}
      className={cn(
        'border-b border-line last:border-b-0 hover:bg-bg-sunk/60',
        highlighted && 'bg-brand-soft/60 ring-1 ring-inset ring-brand/40'
      )}
    >
      <td className="truncate px-2 py-1 font-mono text-ink">
        {leaf}
        {isUdtMember ? <UdtBadge /> : null}
      </td>
      {showFolderColumn && (
        <td className="truncate px-2 py-1 font-mono text-11 text-ink-3">
          {folder || <span className="text-ink-4">—</span>}
        </td>
      )}
      <td className="px-2 py-1 font-mono text-11 text-ink-3">
        {typeLabel ?? <span className="text-ink-4">—</span>}
      </td>
      {showValueColumn && (
        <td className="px-2 py-1 text-right">
          <LiveValueCell value={value} unit={tag.eng_unit} />
        </td>
      )}
    </tr>
  )
}

function LiveValueError({ reason }: { reason: string }): React.ReactElement {
  return (
    <span title={reason} className="inline-flex items-center gap-1 font-mono text-11 text-st-warn">
      <WarningCircleIcon className="h-3 w-3 shrink-0" aria-hidden />
      err
    </span>
  )
}

function LiveValueCell({
  value,
  unit
}: {
  value: PlantTagValue | undefined
  unit: string | undefined
}): React.ReactElement {
  if (value?.error) {
    return <LiveValueError reason={value.error} />
  }
  if (value?.value === '') {
    if (value.quality && value.quality !== 'Good') {
      return <LiveValueError reason={`Quality: ${value.quality}`} />
    }
    return <span className="font-mono text-11 italic text-ink-4">(empty)</span>
  }
  const display = value ? formatLiveValue(value.value) : undefined
  if (display === undefined) {
    // A Bad-quality row without a value is a failed read (dead device,
    // relay timeout, …) — surface it as an error instead of silently
    // rendering nothing.
    if (value?.quality && value.quality !== 'Good') {
      return <LiveValueError reason={`Quality: ${value.quality}`} />
    }
    // No reading at all: never dangle the unit label without a value.
    return <span className="font-mono text-11 text-ink-4">—</span>
  }
  const quality = value?.quality ?? 'Good'
  const stale = quality !== 'Good'
  return (
    <span
      title={stale ? `Quality: ${quality}` : undefined}
      className={cn('font-mono text-11 tabular-nums', stale ? 'text-ink-4' : 'text-brand')}
    >
      {display}
      {unit ? ` ${unit}` : ''}
    </span>
  )
}

function PageNav({
  currentPage,
  totalPages,
  isFetching,
  onPrev,
  onNext
}: {
  currentPage: number
  totalPages: number
  isFetching: boolean
  onPrev: () => void
  onNext: () => void
}): React.ReactElement {
  const atFirst = currentPage <= 0
  const atLast = currentPage >= totalPages - 1
  return (
    <div className="flex items-center gap-2" aria-label="Pagination">
      <span className="whitespace-nowrap text-11 text-ink-4">
        Page {currentPage + 1} of {totalPages}
        {isFetching ? ' · Loading…' : ''}
      </span>
      <PageButton disabled={atFirst} onClick={onPrev} label="Prev" icon="left" />
      <PageButton disabled={atLast} onClick={onNext} label="Next" icon="right" />
    </div>
  )
}

function PageButton({
  disabled,
  onClick,
  label,
  icon
}: {
  disabled: boolean
  onClick: () => void
  label: string
  icon: 'left' | 'right'
}): React.ReactElement {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex items-center gap-1 rounded-md border px-2.5 py-1 text-11 transition-colors',
        disabled
          ? 'cursor-not-allowed border-line text-ink-4'
          : 'cursor-pointer border-line text-ink-2 hover:bg-bg-sunk hover:text-ink focus-visible:border-brand focus-visible:outline-none'
      )}
    >
      {icon === 'left' && <CaretLeftIcon size={10} />}
      {label}
      {icon === 'right' && <CaretRightIcon size={10} />}
    </button>
  )
}

/**
 * Leaf segment of a slash-separated tag path. `BL1/Mix/Tank01/Level` →
 * `Level`. Paths without `/` are returned as-is. Used so the Tag column
 * shows the short identifier and the Folder column (or breadcrumb)
 * carries the prefix.
 */
function leafName(name: string): string {
  const idx = name.lastIndexOf('/')
  return idx >= 0 ? name.slice(idx + 1) : name
}

/**
 * Construct an Ignition-readable tag path for the live-data backend.
 * Native Ignition paths look like `[provider]folder/name`; gateways
 * reject bare `folder/name` even when a `default` provider is
 * configured. Returns `undefined` when no usable path is on the node.
 *
 * Runtime `:Tag` rows, including runtime UDT members, carry the full
 * canonical path in `tag_path`; display names never become live identity.
 */
function tagReadPath(tag: PlantProviderTag): string | undefined {
  const stored = tag.tag_path?.trim()
  return stored || undefined
}

function formatLiveValue(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return String(value)
    const abs = Math.abs(value)
    if (abs >= 100) return value.toLocaleString(undefined, { maximumFractionDigits: 0 })
    if (abs >= 10) return value.toLocaleString(undefined, { maximumFractionDigits: 1 })
    if (abs >= 1) return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
    return value.toLocaleString(undefined, { maximumFractionDigits: 3 })
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'string') {
    return value.length > 24 ? value.slice(0, 23) + '…' : value
  }
  return undefined
}
